import { Edit } from '@carbon/react/icons';
import { useAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';

import type { InterestWorkspaceResponse } from 'types/api-responses';

import { renameWorkspaceLoadableAtom } from './atoms';
import styles from './workspace-name-editor.module.scss';

interface WorkspaceNameEditorProps {
  workspace: InterestWorkspaceResponse;
}

/**
 * Inline-editable workspace name for the dark `WorkspaceShellHeader` bar.
 *
 * Renders the workspace name with a pencil icon button. Clicking the pencil
 * (or the name itself) switches to an inline `<input>` pre-filled with the
 * current name. Pressing Enter or blurring submits the rename if the value
 * changed; Escape cancels. Disabled when the workspace is promoted (frozen).
 *
 * @param {WorkspaceNameEditorProps} props - Component props.
 * @return {JSX.Element} The rendered editable name.
 */
export const WorkspaceNameEditor = ({
  workspace,
}: WorkspaceNameEditorProps): JSX.Element => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(workspace.name);
  const [, renameWorkspace] = useAtom(renameWorkspaceLoadableAtom);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Guards against the Enter→blur double-fire: onKeyDown calls submit(), which
  // on success calls setEditing(false), which unmounts the input synchronously
  // and causes the browser to fire blur before the async submit has settled.
  const submittingRef = useRef(false);
  const promoted = workspace.promoted;

  // Keep local value in sync if the workspace record updates externally
  // (e.g. another tab renames it and the atom refetches).
  useEffect(() => {
    if (!editing) setValue(workspace.name);
  }, [workspace.name, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const startEditing = (): void => {
    if (promoted) return;
    setValue(workspace.name);
    setError(null);
    setEditing(true);
  };

  const cancel = (): void => {
    setEditing(false);
    setValue(workspace.name);
    setError(null);
  };

  const submit = async (): Promise<void> => {
    if (submittingRef.current) return;
    const trimmed = value.trim();
    if (!trimmed || trimmed === workspace.name) {
      cancel();
      return;
    }
    submittingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      await renameWorkspace(workspace.id, trimmed);
      setEditing(false);
    } catch {
      setError("Couldn't rename workspace");
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <span className={styles.editRow}>
        <input
          ref={inputRef}
          className={styles.input}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit();
            if (e.key === 'Escape') cancel();
          }}
          onBlur={() => void submit()}
          disabled={saving}
          aria-label="Workspace name"
          maxLength={255}
        />
        {error && <span className={styles.error}>{error}</span>}
      </span>
    );
  }

  return (
    <span className={styles.nameRow}>
      <span className={styles.name}>{workspace.name}</span>
      {!promoted && (
        <button
          type="button"
          className={styles.editBtn}
          onClick={startEditing}
          aria-label="Rename workspace"
          title="Rename workspace"
        >
          <Edit size={16} />
        </button>
      )}
    </span>
  );
};
